import { useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";
import { BRAND_ATTR, BRAND_LABEL_ATTR } from "../theme/tenant.ts";
import { EsiLockup } from "./EsiMark.tsx";

function subscribe(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [BRAND_ATTR, BRAND_LABEL_ATTR],
  });
  return () => observer.disconnect();
}

/** The tenant logo's label while one is shown (`applyTenantTheme` set the root attributes). */
function tenantLabel(): string | null {
  const root = document.documentElement;
  const label = root.getAttribute(BRAND_LABEL_ATTR)?.trim();
  return root.getAttribute(BRAND_ATTR) === "tenant" && label ? label : null;
}

/** The tenant logo's label when a tenant logo replaces the ESI mark, else `null`; live. */
export function useTenantLogo(): string | null {
  return useSyncExternalStore(subscribe, tenantLabel, () => null);
}

/**
 * The product mark slot: the ESI lockup, or the tenant's logo in its place (never both). The
 * tenant logo is the `--brand-logo` image at its own aspect ratio (`--brand-logo-ratio`), so the
 * slot ends where the logo does, fitted within `tenantClassName`'s height and maximum width and
 * left-aligned, named by the tenant's label.
 */
export function BrandMark({
  width,
  tenantClassName,
  className,
}: {
  /** The ESI lockup's width. */
  width: number;
  /** The tenant logo's box: its height and maximum width (`max-w-*`). */
  tenantClassName: string;
  className?: string;
}) {
  const tenant = useTenantLogo();
  if (tenant) {
    return (
      <span
        role="img"
        aria-label={tenant}
        data-slot="tenant-logo"
        className={cn(
          "block aspect-[var(--brand-logo-ratio,20/3)] bg-(image:--brand-logo) bg-contain bg-left bg-no-repeat",
          tenantClassName,
          className,
        )}
      />
    );
  }
  return <EsiLockup width={width} className={className} />;
}
